import { ORDER } from '../core/systems.js';

/**
 * ===========================================================================
 *  SAVE — the first thing in this game that outlives a battle
 * ===========================================================================
 *
 * DEV_PLAN Step 1. Until this existed nothing a player earned could reach the
 * next battle, which is why `RUBRIC.md` scores `reasonToReplay` at 0 and why
 * `critic_loop_trace` reported `persistenceLayer: false, 0 of 4 keys`.
 *
 * Two rules shape the whole module.
 *
 * 1. THE RECORD IS ALWAYS LIVE; ONLY THE DURABLE WRITE IS GATED.
 *
 *    A headless capture must never read or write real storage, or scenarios
 *    become order-dependent and the determinism guarantee in CLAUDE.md is
 *    gone: scenario A clearing a battle would change what scenario B boots
 *    into. But if headless simply had no save at all, the loop probe could
 *    never observe a battle boundary either.
 *
 *    So the schema, the mutations and `world.save` exist in every mode. A
 *    headless boot starts from `defaultSave()` every single time and never
 *    touches `localStorage`, which is both deterministic AND measurable: the
 *    record still moves when a battle ends, it just starts clean.
 *
 * 2. A MIGRATION NEVER DROPS A FIELD IT DOES NOT UNDERSTAND.
 *
 *    `migrate()` layers a loaded record over the current defaults, so a v1
 *    save read by a v2 build keeps every v1 value and gains v2's new keys at
 *    their defaults. Unknown keys from a NEWER save are carried through
 *    untouched rather than deleted, so downgrading and re-upgrading does not
 *    silently destroy progress.
 */

export const SAVE_KEY = 'nusantara.save';
export const SCHEMA_VERSION = 1;

/** The shape of a fresh record. Every key here is a key `migrate` guarantees. */
export function defaultSave() {
  return {
    version: SCHEMA_VERSION,
    battlesCleared: [],      // battle ids, in the order they were first cleared
    rank: 1,                 // Step 2 drives this
    proficiency: {},         // weapon name -> level; Step 2 drives this
    unlocks: [],             // ids of anything the player has earned
    // RESERVED, and deliberately empty today. `src/ui/hud.js` already reads
    // and writes the live options under its own `sb.settings` key, so filling
    // this one now would create a second source of truth for the same data and
    // whichever loaded last would win. The schema carries the key because
    // DEV_PLAN Step 1 specifies it; consolidating the two stores is a HUD
    // change and belongs with the step that touches the options screen.
    settings: {},
    stats: { battles: 0, kills: 0, bestChain: 0, playSeconds: 0 },
  };
}

/**
 * Bring any record up to the current schema.
 *
 * Layering order matters: defaults first so every known key exists, then the
 * loaded values so nothing the player earned is lost, then a repair pass for
 * fields whose TYPE is wrong (a hand-edited or truncated record) rather than
 * merely absent.
 */
export function migrate(raw) {
  const base = defaultSave();
  if (!raw || typeof raw !== 'object') return base;
  const out = { ...base, ...raw };
  // nested objects would otherwise be replaced wholesale by a partial one
  out.settings = { ...base.settings, ...(raw.settings || {}) };
  out.proficiency = { ...base.proficiency, ...(raw.proficiency || {}) };
  out.stats = { ...base.stats, ...(raw.stats || {}) };
  // type repair — a corrupt field is reset, a merely missing one already has
  // its default from the spread above
  if (!Array.isArray(out.battlesCleared)) out.battlesCleared = [];
  if (!Array.isArray(out.unlocks)) out.unlocks = [];
  if (!Number.isFinite(out.rank) || out.rank < 1) out.rank = base.rank;
  out.version = SCHEMA_VERSION;
  return out;
}

/** Read and parse, or null. Never throws: storage can be disabled or full. */
function readStore() {
  try {
    if (typeof localStorage === 'undefined') return null;
    const s = localStorage.getItem(SAVE_KEY);
    return s ? JSON.parse(s) : null;
  } catch (e) { return null; }
}

function writeStore(rec) {
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(SAVE_KEY, JSON.stringify(rec));
    return true;
  } catch (e) { return false; }   // private mode, quota, disabled storage
}

/**
 * @param {{persist?:boolean}} o  `persist` false keeps the record in memory
 *        only. main.js passes `!headless`.
 */
export function createSave(o = {}) {
  const persist = o.persist !== false;
  let rec = defaultSave();

  return {
    name: 'save', order: ORDER.MISSION,

    init(w) {
      if (persist) rec = migrate(readStore());

      const api = {
        /** True when this record is backed by real storage. */
        get persistent() { return persist; },
        get version() { return rec.version; },

        /** A plain copy. The loop probe reads this; nothing may mutate it. */
        snapshot() { return JSON.parse(JSON.stringify(rec)); },

        /* The schema itself, reachable from a running world. A settings screen
         * importing a pasted record needs `migrate` as much as the loader
         * does, and the contract test needs it to prove the layering rule
         * without importing this module into a page it cannot import into. */
        migrate,
        defaults: defaultSave,

        get(key) { return rec[key]; },
        set(key, value) { rec[key] = value; return api.flush(); },

        /** Record a cleared battle once, and count the run. */
        recordBattle(id, result, stats) {
          rec.stats.battles++;
          if (stats) {
            rec.stats.kills += (stats.kills | 0);
            rec.stats.bestChain = Math.max(rec.stats.bestChain, stats.maxChain | 0);
            rec.stats.playSeconds += Math.round(stats.elapsed || 0);
          }
          if (result === 'victory' && id && rec.battlesCleared.indexOf(id) < 0) {
            rec.battlesCleared.push(id);
          }
          return api.flush();
        },

        /** Grant an unlock once. Returns true if it was new. */
        unlock(id) {
          if (!id || rec.unlocks.indexOf(id) >= 0) return false;
          rec.unlocks.push(id);
          api.flush();
          return true;
        },

        flush() { return persist ? writeStore(rec) : false; },

        /** Wipe to defaults. Used by the UI and by the contract test. */
        reset() { rec = defaultSave(); api.flush(); return api.snapshot(); },
      };

      w.save = api;

      /* The mission already announces itself on the bus, so nothing in
       * mission.js needs to know this module exists. */
      w.bus.on('mission:end', (result, reason) => {
        const m = w.mission;
        if (!m) return;
        const i = m.info();
        // Which battle was this? Nothing publishes it yet: `select.js` keeps
        // its {heroId, arenaId} choice internal and `mission.reset/start` take
        // no stage id, so every run currently records under one id and
        // `battlesCleared` collapses to a single entry. Recording the honest
        // fallback rather than inventing an id -- the plumbing belongs to Step
        // 10 (free mode), which is the step that actually needs per-battle
        // records, and this line starts working the moment it lands.
        const id = m.battleId || w.stageId || 'campaign';
        api.recordBattle(id, result, { kills: i.kills, maxChain: i.maxChain, elapsed: i.elapsed });
      });
    },
  };
}
